import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle, ChevronLeft, ChevronRight, Clock, RefreshCw, ShieldAlert, Filter, Download, Square, CheckSquare, CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/formatters';
import { exportCsv } from '../lib/exportCsv';
import { useAuthStore } from '../store/auth.store';
import { useToasts, ToastContainer } from '../components/Toast';

function SeverityBadge({ severity }: { severity: string }) {
  const critical = severity === 'CRITICAL';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-semibold ${critical ? 'badge-critical' : 'badge-warning'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${critical ? 'bg-red-400 pulse-dot' : 'bg-amber-400'}`} />
      {severity}
    </span>
  );
}

function StatusBadge({ resolvedAt }: { resolvedAt: string | null }) {
  if (resolvedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium badge-active">
        <CheckCircle className="w-3 h-3" /> Resolved
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium badge-warning">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 pulse-dot" /> Open
    </span>
  );
}

export function AnomaliesPage() {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.role);
  const isReadOnly = role === 'ADMIN_READONLY';
  const { toasts, push, dismiss } = useToasts();

  const { data: anomalies = [], isLoading, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['anomalies'],
    queryFn: () => api.get('/analytics/platform/anomalies').then((r) => r.data).catch(() => []),
    refetchInterval: 30_000,
  });

  // ── Real-time notification: detect new open anomalies since last poll ──
  const prevOpenIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const openAnomalies = (anomalies as Record<string, string>[]).filter((a) => !a['resolvedAt']);
    const prevIds = prevOpenIdsRef.current;
    if (prevIds.size > 0) {
      const newOnes = openAnomalies.filter((a) => !prevIds.has(a['id']));
      if (newOnes.length > 0) {
        const critical = newOnes.filter((a) => a['severity'] === 'CRITICAL');
        push({
          kind: critical.length > 0 ? 'error' : 'warning',
          title: `${newOnes.length} new anomaly flag${newOnes.length > 1 ? 's' : ''} detected`,
          body: critical.length > 0
            ? `${critical.length} CRITICAL — review immediately`
            : `Severity: ${newOnes[0]['severity']}`,
          durationMs: 8000,
        });
      }
    }
    prevOpenIdsRef.current = new Set(openAnomalies.map((a) => a['id']));
  }, [anomalies]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCount = (anomalies as Record<string, string>[]).filter((a) => !a['resolvedAt']).length;
  const criticalCount = (anomalies as Record<string, string>[]).filter((a) => a['severity'] === 'CRITICAL' && !a['resolvedAt']).length;
  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : null;

  const ANOMALIES_PER_PAGE = 25;
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'open' | 'resolved' | ''>('open');
  const filteredAnomalies = useMemo(() => {
    return (anomalies as Record<string, string>[]).filter((a) => {
      if (severityFilter && a['severity'] !== severityFilter) return false;
      if (statusFilter === 'open' && a['resolvedAt']) return false;
      if (statusFilter === 'resolved' && !a['resolvedAt']) return false;
      return true;
    });
  }, [anomalies, severityFilter, statusFilter]);
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filteredAnomalies.length / ANOMALIES_PER_PAGE));
  const paginatedAnomalies = useMemo(
    () => filteredAnomalies.slice((page - 1) * ANOMALIES_PER_PAGE, page * ANOMALIES_PER_PAGE),
    [filteredAnomalies, page],
  );

  // ── Bulk selection ──
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const pageIds = useMemo(() => paginatedAnomalies.map((a: Record<string, string>) => a['id']), [paginatedAnomalies]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id: string) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id: string) => selectedIds.has(id));

  const toggleOne = (id: string) => setSelectedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const togglePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) { pageIds.forEach((id: string) => next.delete(id)); }
      else { pageIds.forEach((id: string) => next.add(id)); }
      return next;
    });
  };

  // ── Bulk resolve mutation ──
  const resolveMutation = useMutation({
    mutationFn: (ids: string[]) => api.patch('/analytics/anomalies/bulk-resolve', { ids }),
    onSuccess: (_, ids) => {
      push({ kind: 'success', title: `${ids.length} anomal${ids.length === 1 ? 'y' : 'ies'} resolved` });
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: ['anomalies'] });
    },
    onError: () => push({ kind: 'error', title: 'Bulk resolve failed', body: 'Please try again.' }),
  });

  return (
    <div className="p-8">
      <ToastContainer toasts={toasts} dismiss={dismiss} />
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-xl font-bold text-white">Anomaly Flags</h2>
            <p className="text-sm text-slate-600 mt-0.5">Auto-refreshes every 30s</p>
          </div>
          {/* Live counters */}
          {!isLoading && (
            <div className="flex gap-3 ml-4">
              {criticalCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl badge-critical text-xs font-semibold">
                  <ShieldAlert className="w-3.5 h-3.5" />
                  {criticalCount} Critical
                </div>
              )}
              {openCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl badge-warning text-xs font-semibold">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {openCount} Open
                </div>
              )}
              {openCount === 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl badge-active text-xs font-semibold">
                  <CheckCircle className="w-3.5 h-3.5" />
                  All Clear
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && !isReadOnly && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={() => resolveMutation.mutate(Array.from(selectedIds))}
              disabled={resolveMutation.isPending}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 hover:bg-emerald-500/25 transition-all disabled:opacity-40"
            >
              {resolveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Resolve {selectedIds.size} selected
            </motion.button>
          )}
          <button
            onClick={() => exportCsv(
              filteredAnomalies,
              `anomalies-${new Date().toISOString().slice(0, 10)}.csv`,
              [
                { key: 'id',         label: 'ID' },
                { key: 'worldId',    label: 'World ID' },
                { key: 'playerId',   label: 'Player ID' },
                { key: 'flagType',   label: 'Flag Type' },
                { key: 'severity',   label: 'Severity' },
                { key: 'detectedAt', label: 'Detected At' },
                { key: 'resolvedAt', label: 'Resolved At' },
              ],
            )}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300 transition-colors px-3 py-1.5 rounded-lg border border-white/5 hover:border-cyan-500/20 bg-white/[0.02]"
          >
            <Download className="w-3.5 h-3.5" /> Export
          </button>
          {lastUpdated && <span className="text-xs text-slate-600">Updated {lastUpdated}</span>}
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300 transition-colors px-3 py-1.5 rounded-lg border border-white/5 hover:border-cyan-500/20 bg-white/[0.02]"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="glass-card overflow-hidden"
      >
        <div className="h-px bg-gradient-to-r from-transparent via-red-500/20 to-transparent" />

        {/* Filters bar */}
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/[0.04]">
          <Filter className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
          <select
            value={severityFilter}
            onChange={(e) => { setSeverityFilter(e.target.value); setPage(1); }}
            className="bg-transparent text-sm text-slate-300 focus:outline-none cursor-pointer"
          >
            <option value="" className="bg-[#0b1120]">All severities</option>
            <option value="CRITICAL" className="bg-[#0b1120]">Critical</option>
            <option value="HIGH" className="bg-[#0b1120]">High</option>
            <option value="MEDIUM" className="bg-[#0b1120]">Medium</option>
            <option value="LOW" className="bg-[#0b1120]">Low</option>
          </select>
          <span className="text-slate-700">|</span>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as 'open' | 'resolved' | ''); setPage(1); }}
            className="bg-transparent text-sm text-slate-300 focus:outline-none cursor-pointer"
          >
            <option value="open" className="bg-[#0b1120]">Open only</option>
            <option value="resolved" className="bg-[#0b1120]">Resolved</option>
            <option value="" className="bg-[#0b1120]">All statuses</option>
          </select>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              {!isReadOnly && (
                <th className="px-4 py-3.5 w-8">
                  <button onClick={togglePage} className="text-slate-500 hover:text-cyan-300 transition-colors">
                    {allPageSelected
                      ? <CheckSquare className="w-3.5 h-3.5 text-cyan-400" />
                      : somePageSelected
                        ? <CheckSquare className="w-3.5 h-3.5 text-slate-500" />
                        : <Square className="w-3.5 h-3.5" />}
                  </button>
                </th>
              )}
              {['World', 'Player', 'Type', 'Severity', 'Detected', 'Status'].map((h) => (
                <th key={h} className="text-left px-5 py-3.5 text-[10px] font-semibold uppercase tracking-widest text-slate-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: isReadOnly ? 6 : 7 }).map((__, j) => (
                    <td key={j} className="px-5 py-4"><div className="shimmer h-4 rounded w-3/4" /></td>
                  ))}
                </tr>
              ))
            ) : filteredAnomalies.length === 0 ? (
              <tr>
                <td colSpan={isReadOnly ? 6 : 7} className="px-5 py-16 text-center">
                  <div className="w-12 h-12 rounded-full badge-active flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                  </div>
                  <p className="text-slate-400 text-sm font-medium">{severityFilter ? `No ${severityFilter} anomaly flags` : 'No anomaly flags match current filters'}</p>
                  <p className="text-slate-700 text-xs mt-1">System is operating normally</p>
                </td>
              </tr>
            ) : (
              <AnimatePresence>
                {paginatedAnomalies.map((a: Record<string, string>, idx: number) => (
                  <motion.tr
                    key={a['id']}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.04, duration: 0.3 }}
                    className={`table-row-hover ${selectedIds.has(a['id']) ? 'bg-cyan-500/5' : ''}`}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                  >
                    {!isReadOnly && (
                      <td className="px-4 py-4">
                        <button onClick={() => toggleOne(a['id'])} className="text-slate-500 hover:text-cyan-300">
                          {selectedIds.has(a['id'])
                            ? <CheckSquare className="w-3.5 h-3.5 text-cyan-400" />
                            : <Square className="w-3.5 h-3.5" />}
                        </button>
                      </td>
                    )}
                    <td className="px-5 py-4 font-mono text-xs text-slate-500">{a['worldId']?.slice(0, 8)}…</td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-500">{a['playerId']?.slice(0, 8) ?? '—'}</td>
                    <td className="px-5 py-4">
                      <span className="text-sm font-medium text-slate-200">{a['flagType']?.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-5 py-4"><SeverityBadge severity={a['severity']} /></td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 text-slate-600 text-xs">
                        <Clock className="w-3 h-3" />
                        {formatDateTime(a['detectedAt'])}
                      </div>
                    </td>
                    <td className="px-5 py-4"><StatusBadge resolvedAt={a['resolvedAt']} /></td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-white/[0.04]">
            <span className="text-xs text-slate-600">
              Page {page} of {totalPages} &bull; {filteredAnomalies.length}{(severityFilter || statusFilter) ? ` / ${(anomalies as Record<string, string>[]).length}` : ''} flags
              {selectedIds.size > 0 && <span className="text-cyan-400 ml-2">({selectedIds.size} selected)</span>}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

